import { useState, useEffect } from 'react'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { TextArea } from '@progress/kendo-react-inputs'
import { Label } from '@progress/kendo-react-labels'
import { Upload, UploadOnStatusChangeEvent } from '@progress/kendo-react-upload'
import { SiteReport, CreateSiteReport, UpdateSiteReport } from '../../../data/interfaces'
import { getDocument, downloadDocument } from '../../../data/fileData'

const apiURL = process.env.REACT_APP_API_URL

interface SiteReportFormProps {
  report: SiteReport | null
  jobId: number
  onSubmit: (data: CreateSiteReport | UpdateSiteReport) => void
  onCancel: () => void
  onAttachmentUploaded?: () => void
}

const fieldStyle = { marginBottom: '16px' }
const labelStyle = { fontWeight: 600, display: 'block', marginBottom: '4px' }
const textAreaStyle = { width: '100%', resize: 'vertical' as const }

const SiteReportForm = (props: SiteReportFormProps) => {
  const token = localStorage.getItem('authToken')

  const [reportDate, setReportDate] = useState<Date>(new Date())
  const [weatherAndImpact, setWeatherAndImpact] = useState<string>('')
  const [plantAndEquipment, setPlantAndEquipment] = useState<string>('')
  const [workActivities, setWorkActivities] = useState<string>('')
  const [delaysIssuesConstraints, setDelaysIssuesConstraints] = useState<string>('')
  const [instructionsVerbalDiscussions, setInstructionsVerbalDiscussions] = useState<string>('')
  const [dayworksLabourMaterial, setDayworksLabourMaterial] = useState<string>('')

  useEffect(() => {
    if (props.report) {
      setReportDate(new Date(props.report.reportDate))
      setWeatherAndImpact(props.report.weatherAndImpact ?? '')
      setPlantAndEquipment(props.report.plantAndEquipment ?? '')
      setWorkActivities(props.report.workActivities ?? '')
      setDelaysIssuesConstraints(props.report.delaysIssuesConstraints ?? '')
      setInstructionsVerbalDiscussions(props.report.instructionsVerbalDiscussions ?? '')
      setDayworksLabourMaterial(props.report.dayworksLabourMaterial ?? '')
    } else {
      setReportDate(new Date())
      setWeatherAndImpact('')
      setPlantAndEquipment('')
      setWorkActivities('')
      setDelaysIssuesConstraints('')
      setInstructionsVerbalDiscussions('')
      setDayworksLabourMaterial('')
    }
  }, [props.report])

  const handleSubmit = () => {
    if (props.report) {
      const dto: UpdateSiteReport = {
        siteReportId: props.report.siteReportId,
        jobId: props.jobId,
        reportDate: reportDate.toISOString(),
        weatherAndImpact: weatherAndImpact || null,
        plantAndEquipment: plantAndEquipment || null,
        workActivities: workActivities || null,
        delaysIssuesConstraints: delaysIssuesConstraints || null,
        instructionsVerbalDiscussions: instructionsVerbalDiscussions || null,
        dayworksLabourMaterial: dayworksLabourMaterial || null,
      }
      props.onSubmit(dto)
    } else {
      const dto: CreateSiteReport = {
        jobId: props.jobId,
        reportDate: reportDate.toISOString(),
        weatherAndImpact: weatherAndImpact || null,
        plantAndEquipment: plantAndEquipment || null,
        workActivities: workActivities || null,
        delaysIssuesConstraints: delaysIssuesConstraints || null,
        instructionsVerbalDiscussions: instructionsVerbalDiscussions || null,
        dayworksLabourMaterial: dayworksLabourMaterial || null,
      }
      props.onSubmit(dto)
    }
  }

  const handleUploadStatusChange = (e: UploadOnStatusChangeEvent) => {
    if (e.response?.status === 200 && props.onAttachmentUploaded) {
      props.onAttachmentUploaded()
    }
  }

  const isEdit = !!props.report

  return (
    <Dialog
      title={isEdit ? 'Edit Site Report' : 'New Site Report'}
      onClose={props.onCancel}
      appendTo={document.body}
      width={700}
      style={{ maxHeight: '90vh' }}
    >
      <div style={{ padding: '16px 8px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>

        {/* Date */}
        <div style={fieldStyle}>
          <Label style={labelStyle}>Date</Label>
          <DatePicker
            value={reportDate}
            onChange={(e) => e.value && setReportDate(e.value)}
            format="dd/MM/yyyy"
            style={{ width: '100%' }}
          />
        </div>

        {/* Weather & Impact */}
        <div style={fieldStyle}>
          <Label style={labelStyle}>Weather &amp; Impact to Works</Label>
          <TextArea
            value={weatherAndImpact}
            onChange={(e) => setWeatherAndImpact(e.value)}
            rows={3}
            style={textAreaStyle}
            placeholder="Describe weather conditions and any impact on works..."
          />
        </div>

        {/* Plant & Equipment */}
        <div style={fieldStyle}>
          <Label style={labelStyle}>Plant &amp; Equipment on Site</Label>
          <TextArea
            value={plantAndEquipment}
            onChange={(e) => setPlantAndEquipment(e.value)}
            rows={3}
            style={textAreaStyle}
            placeholder="List plant and equipment on site..."
          />
        </div>

        {/* Work Activities */}
        <div style={fieldStyle}>
          <Label style={labelStyle}>Work Activities Completed Today</Label>
          <TextArea
            value={workActivities}
            onChange={(e) => setWorkActivities(e.value)}
            rows={4}
            style={textAreaStyle}
            placeholder="Describe work activities completed today..."
          />
        </div>

        {/* Delays / Issues / Constraints */}
        <div style={fieldStyle}>
          <Label style={labelStyle}>Delays / Issues / Constraints</Label>
          <TextArea
            value={delaysIssuesConstraints}
            onChange={(e) => setDelaysIssuesConstraints(e.value)}
            rows={3}
            style={textAreaStyle}
            placeholder="Describe any delays, issues or constraints..."
          />
        </div>

        {/* Instructions / Verbal Discussions */}
        <div style={fieldStyle}>
          <Label style={labelStyle}>Instructions / Verbal Discussions</Label>
          <TextArea
            value={instructionsVerbalDiscussions}
            onChange={(e) => setInstructionsVerbalDiscussions(e.value)}
            rows={3}
            style={textAreaStyle}
            placeholder="Record any instructions or verbal discussions..."
          />
        </div>

        {/* Dayworks */}
        <div style={fieldStyle}>
          <Label style={labelStyle}>Dayworks Carried Out &ndash; Labour / Material</Label>
          <TextArea
            value={dayworksLabourMaterial}
            onChange={(e) => setDayworksLabourMaterial(e.value)}
            rows={3}
            style={textAreaStyle}
            placeholder="Describe any dayworks labour or materials..."
          />
        </div>

        {/* Attachments — only available in edit mode once the record is saved */}
        {isEdit && props.report && (
          <div style={fieldStyle}>
            <Label style={labelStyle}>Attachments</Label>
            {props.report.attachments && props.report.attachments.length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                {props.report.attachments.map((doc) => (
                  <div
                    key={doc.documentId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '4px 0',
                      fontSize: '14px',
                    }}
                  >
                    <span>📎</span>
                    <button
                      type="button"
                      onClick={() => getDocument(doc.documentId, doc.fileName)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: '#1e2a4e',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: '14px',
                      }}
                    >
                      {doc.fileName}
                    </button>
                    <button
                      type="button"
                      title="Download file"
                      onClick={() => downloadDocument(doc.documentId, doc.fileName)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0 4px',
                        color: '#1e2a4e',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      ⬇
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Upload
              saveUrl={`https://${apiURL}/Files/SiteReport/${props.report.siteReportId}`}
              saveHeaders={{ Authorization: 'Bearer ' + token }}
              onStatusChange={handleUploadStatusChange}
              multiple={true}
              withCredentials={false}
            />
          </div>
        )}
        {!isEdit && (
          <p style={{ fontSize: '13px', color: '#666', fontStyle: 'italic', margin: '8px 0 0' }}>
            Save the report first to add attachments.
          </p>
        )}
      </div>

      <DialogActionsBar>
        <button
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
          onClick={props.onCancel}
        >
          Cancel
        </button>
        <button
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
          onClick={handleSubmit}
        >
          {isEdit ? 'Save Changes' : 'Create Report'}
        </button>
      </DialogActionsBar>
    </Dialog>
  )
}

export default SiteReportForm
